<script lang="ts">
  import { cartStore } from '../stores/cart';
  import { formatCurrency } from '../utils/format';
  import { frappeClient } from '../services/frappe';
  import { invoicesStorage, settingsStorage } from '../services/storage';
  import { printReceipt } from '../utils/receipt';
  import { printToBluetoothPrinter } from '../utils/printer';
  import { generateNextInvoiceNumber } from '../utils/invoiceNumber';
  import NumberPad from './NumberPad.svelte';
  import type { SalesInvoice, PaymentMethod } from '../types';

  interface Props {
    total: number;
    onClose: () => void;
    onSuccess?: () => void;
  }

  let { total, onClose, onSuccess }: Props = $props();

  let paymentMethod = $state<'cash' | 'card'>('cash');
  let cashReceived = $state(0);
  let change = $state(0);
  let isProcessing = $state(false);
  let error = $state<string | null>(null);
  let successMessage = $state<string | null>(null);
  let showPrintPrompt = $state(false);
  let checkoutCompleted = $state(false);
  let hasFinalizedCheckout = $state(false);

  // Berechne Wechselgeld bei Barzahlung
  $effect(() => {
    if (paymentMethod === 'cash' && cashReceived > 0) {
      change = Math.max(0, cashReceived - total);
    } else {
      change = 0;
    }
  });

  async function handleCheckout() {
    if (checkoutCompleted) {
      return;
    }

    if (paymentMethod === 'cash' && cashReceived < total) {
      error = 'Bitte geben Sie den vollen Betrag ein.';
      return;
    }

    isProcessing = true;
    error = null;
    successMessage = null;

    try {
      // Erstelle Payment Method
      const payment: PaymentMethod = {
        type: paymentMethod === 'cash' ? 'cash' : 'card',
        amount: total,
        status: 'completed'
      };

      // Generiere Belegnummer
      const invoiceNumber = generateNextInvoiceNumber();

      // Erstelle Sales Invoice mit eindeutiger ID
      const invoiceId = `inv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const invoice: SalesInvoice = {
        id: invoiceId,
        invoice_number: invoiceNumber,
        items: $cartStore.items,
        subtotal: total,
        total,
        payments: [payment],
        status: 'paid',
        created_at: new Date().toISOString(),
        synced: false
      };

      // Speichere lokal
      invoicesStorage.add(invoice);
      savedInvoice = invoice;

      // Versuche zu synchronisieren (falls online)
      if (navigator.onLine) {
        try {
          const settings = settingsStorage.load();
          const response = await frappeClient.syncInvoice(invoice, {
            revenue_account: settings.revenue_account,
            cash_account: settings.cash_account,
            sumup_account: settings.sumup_account,
            payment_fees_account: settings.payment_fees_account,
            sumup_fee_percentage: settings.sumup_fee_percentage,
            test_mode: settings.test_mode
          });

          // Speichere das 'name' Feld aus der Frappe Response als frappe_name
          const updatedInvoice = {
            ...invoice,
            frappe_name: response.name,
            synced: true,
            synced_at: new Date().toISOString()
          };
          invoicesStorage.update(invoiceId, updatedInvoice);
          savedInvoice = updatedInvoice;
          successMessage = 'Rechnung erfolgreich gespeichert und übertragen!';
        } catch (syncError) {
          // Offline oder Sync-Fehler - wird später synchronisiert
          successMessage = 'Rechnung gespeichert. Wird synchronisiert wenn online.';
          console.warn('Sync fehlgeschlagen, wird später wiederholt:', syncError);
        }
      } else {
        successMessage = 'Rechnung gespeichert. Wird synchronisiert wenn online.';
      }

      checkoutCompleted = true;
      showPrintPrompt = true;
      isProcessing = false;
    } catch (err) {
      error = err instanceof Error ? err.message : 'Fehler beim Speichern der Rechnung';
      isProcessing = false;
    }
  }

  let savedInvoice: SalesInvoice | null = $state(null);

  async function handlePrint(): Promise<boolean> {
    let success = false;
    try {
      isProcessing = true;
      error = null;

      const invoiceToPrint = savedInvoice || {
        id: 'preview',
        items: $cartStore.items,
        subtotal: total,
        total,
        payments: [
          {
            type: paymentMethod === 'cash' ? 'cash' : 'card',
            amount: total,
            status: 'completed'
          }
        ],
        status: 'draft',
        created_at: new Date().toISOString(),
        synced: false
      };

      // Versuche zuerst Bluetooth-Druck, sonst Fallback zu Anzeige
      await printToBluetoothPrinter(invoiceToPrint);
      success = true;
    } catch (err) {
      console.error('Fehler beim Drucken/Anzeigen des Belegs:', err);
      error = err instanceof Error ? err.message : 'Fehler beim Anzeigen des Belegs';
    } finally {
      isProcessing = false;
    }

    return success;
  }

  async function handlePrintAndFinalize() {
    showPrintPrompt = false;
    const printed = await handlePrint();
    if (!printed) {
      // Druck fehlgeschlagen - erneutes Nachfragen ermöglichen
      showPrintPrompt = true;
      return;
    }
    finalizeCheckout();
  }

  function skipPrintAndFinalize() {
    showPrintPrompt = false;
    finalizeCheckout();
  }

  function finalizeCheckout() {
    if (hasFinalizedCheckout) {
      return;
    }
    hasFinalizedCheckout = true;
    cartStore.clear();
    if (onSuccess) {
      onSuccess();
    }
    onClose();
  }

  function handleCloseRequest() {
    if (checkoutCompleted && !hasFinalizedCheckout) {
      finalizeCheckout();
    } else {
      onClose();
    }
  }

  function setCashAmount(amount: number) {
    cashReceived = Math.max(0, amount);
  }
</script>

<!-- Overlay -->
<div
  class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
  onclick={handleCloseRequest}
  role="button"
  tabindex="0"
  onkeypress={(e) => e.key === 'Escape' && handleCloseRequest()}
  aria-label="Schließen"
>
  <!-- Modal -->
  <div
    class="bg-white rounded-lg shadow-xl max-w-2xl w-full {showPrintPrompt
      ? 'min-h-[300px]'
      : 'max-h-[90vh]'} overflow-y-auto p-4 md:p-6 relative"
    onclick={(e) => e.stopPropagation()}
    role="dialog"
    onkeypress={(e) => e.key === 'Enter' && handleCheckout()}
    aria-modal="true"
    tabindex="0"
  >
    {#if !showPrintPrompt}
      <div>
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg md:text-xl font-bold text-gray-900">Kassendialog</h2>
          <button
            onclick={handleCloseRequest}
            class="text-gray-500 hover:text-gray-700 flex-shrink-0"
            aria-label="Schließen"
            disabled={isProcessing}
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Total -->
        <div class="mb-4 p-4 bg-gray-50 rounded-lg border-2 border-gray-300">
          <div class="text-center">
            <div class="text-sm text-gray-600 mb-1">Gesamtbetrag</div>
            <div class="text-3xl font-bold text-primary-600">{formatCurrency(total)}</div>
          </div>
        </div>

        <!-- Payment Method Selection -->
        <div class="mb-4">
          <p class="block text-xs md:text-sm font-medium text-gray-700 mb-2">Zahlungsmethode</p>
          <div class="grid grid-cols-2 gap-3">
            <button
              type="button"
              onclick={() => {
                paymentMethod = 'cash';
                cashReceived = 0;
              }}
              class="p-3 rounded-lg border-2 transition-all touch-manipulation flex items-center justify-center gap-2 {paymentMethod ===
              'cash'
                ? 'border-primary-600 bg-primary-50 text-primary-700'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}"
              disabled={isProcessing}
            >
              <svg
                class="w-6 h-6 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <div class="font-semibold">Bar</div>
            </button>
            <button
              type="button"
              onclick={() => {
                paymentMethod = 'card';
                cashReceived = 0;
              }}
              class="p-3 rounded-lg border-2 transition-all touch-manipulation flex items-center justify-center gap-2 {paymentMethod ===
              'card'
                ? 'border-primary-600 bg-primary-50 text-primary-700'
                : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}"
              disabled={isProcessing}
            >
              <svg
                class="w-6 h-6 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
              <div class="font-semibold">Karte</div>
            </button>
          </div>
        </div>

        <!-- Cash Input (nur bei Barzahlung) -->
        {#if paymentMethod === 'cash'}
          <div class="mb-4">
            <p class="block text-xs md:text-sm font-medium text-gray-700 mb-2">Erhalten</p>
            <div class="flex gap-3 md:gap-4">
              <!-- Left Column: Display, Quick Buttons, Change -->
              <div class="flex-1 min-w-0">
                <!-- Display -->
                <div class="mb-2 p-3 bg-gray-50 rounded-lg border-2 border-gray-300">
                  <div class="text-center">
                    <div class="text-xl md:text-2xl font-bold text-primary-600">
                      {formatCurrency(cashReceived)}
                    </div>
                  </div>
                </div>

                <!-- Quick Buttons -->
                <div class="flex gap-2 mb-2">
                  <button
                    type="button"
                    onclick={() => setCashAmount(total)}
                    class="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-sm touch-manipulation"
                    disabled={isProcessing}
                  >
                    Genau
                  </button>
                  <button
                    type="button"
                    onclick={() => setCashAmount(Math.ceil(total / 5) * 5)}
                    class="flex-1 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-sm touch-manipulation"
                    disabled={isProcessing}
                  >
                    Aufrunden
                  </button>
                </div>

                {#if change > 0}
                  <div class="p-2 bg-green-50 rounded-lg border-2 border-green-200">
                    <div class="text-center">
                      <div class="text-xs text-gray-600 mb-1">Wechselgeld</div>
                      <div class="text-lg font-bold text-green-700">{formatCurrency(change)}</div>
                    </div>
                  </div>
                {/if}
              </div>

              <!-- Right Column: Number Pad -->
              <div class="w-[40%] flex-shrink-0">
                <NumberPad value={cashReceived} onInput={setCashAmount} />
              </div>
            </div>
          </div>
        {:else}
          <div class="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div class="text-sm text-green-800">
              Kartenzahlung mit SumUp Terminal durchführen, dann hier mit "Bezahlen & Speichern"
              abschließen.
              <br />
            </div>
          </div>
        {/if}

        <!-- Error Message -->
        {#if error}
          <div class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div class="text-sm text-red-800">{error}</div>
          </div>
        {/if}

        <!-- Success Message -->
        {#if successMessage}
          <div class="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div class="text-sm text-green-800">{successMessage}</div>
          </div>
        {/if}

        <!-- Actions -->
        <div
          class="grid grid-cols-2 gap-2 sticky bottom-0 bg-white pt-2 pb-1 -mx-4 md:-mx-6 px-4 md:px-6 border-t border-gray-200 mt-4"
        >
          <button
            onclick={handleCheckout}
            disabled={isProcessing ||
              checkoutCompleted ||
              (paymentMethod === 'cash' && cashReceived < total)}
            class="w-full px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium text-base touch-manipulation disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {#if isProcessing}
              Wird verarbeitet...
            {:else}
              Bezahlen & Speichern
            {/if}
          </button>
          <button
            onclick={handlePrint}
            disabled={isProcessing || checkoutCompleted}
            class="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium text-sm touch-manipulation disabled:bg-gray-100 disabled:cursor-not-allowed"
          >
            Beleg anzeigen
          </button>
        </div>
      </div>
    {/if}

    {#if showPrintPrompt}
      <div class="absolute inset-0 flex items-center justify-center p-6">
        <div
          class="w-full max-w-md rounded-xl border border-blue-200 bg-white p-8 shadow-2xl text-center"
        >
          <p class="text-lg md:text-xl font-semibold text-blue-900 mb-3">
            Soll der Beleg jetzt gedruckt werden?
          </p>
          <p class="text-sm md:text-base text-blue-700 mb-6">
            Du kannst den Beleg auch später erneut über „Beleg anzeigen" drucken.
          </p>
          <div class="flex flex-col gap-3 sm:flex-row">
            <button
              onclick={handlePrintAndFinalize}
              class="flex-1 rounded-lg bg-primary-600 px-4 py-3 text-white font-medium shadow hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              disabled={isProcessing}
            >
              Ja, Beleg drucken
            </button>
            <button
              onclick={skipPrintAndFinalize}
              class="flex-1 rounded-lg bg-gray-200 px-4 py-3 text-gray-800 font-medium shadow hover:bg-gray-300"
            >
              Nein, schließen
            </button>
          </div>
        </div>
      </div>
    {/if}
  </div>
</div>
